import * as React from 'react';
import { PercentageOutlined, RiseOutlined } from '@ant-design/icons';
import Alert from 'antd/lib/alert';
import Checkbox from 'antd/lib/checkbox';
import Descriptions from 'antd/lib/descriptions';
import Flex from 'antd/lib/flex';
import InputNumber from 'antd/lib/input-number';
import Modal from 'antd/lib/modal';
import Popconfirm from 'antd/lib/popconfirm';
import Progress from 'antd/lib/progress';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import { StepRunUpdatable, UpdateExpoEASRolloutRequest } from '@/actions/stepRun';
import PermissionedActionButton from '@/components/HOCs/PermissionedActionButton';
import {
    RolloutStatusActions,
    RolloutStatusCard,
    RolloutStatusMetric,
} from '@/components/monitoring/RolloutStatusCard';
import { antGrey, runwayColors } from '@/components/styles/globalStyles';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { AutomationType, ExpoEASRolloutData, IntegrationType, RolloutAction, RunwayResponseType } from '@/types';
import { StepRunComponentProps } from '@/typescriptHelpers';
import { globalMessage } from '@/utils/globalMessage';
import { useCallUpdatable } from '@/utils/hooks/helpers';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import { selectAutomationsByType } from '@/selectors/selectors';

interface ExpoEASRolloutStepProps {
    stepRunComponentProps: StepRunComponentProps;
    data?: ExpoEASRolloutData;
}

const rolloutActionButtonStyle = { width: '100%', paddingInline: 8 };

const ExpoEASRolloutDetailsCard: React.FC<{ data?: ExpoEASRolloutData }> = ({ data }) => (
    <div
        style={{
            padding: 18,
            borderRadius: 8,
            border: `1px solid ${antGrey.grey5}`,
            height: '100%',
            boxSizing: 'border-box',
            minWidth: 0,
        }}
    >
        <Text style={{ color: antGrey.grey7 }}>Expo rollout details</Text>
        <Descriptions column={1} size="small" style={{ marginTop: 24 }}>
            {data?.sourceGroup && (
                <Descriptions.Item label="Source group">
                    <Space wrap>
                        <Text strong>{data.sourceGroup.branchName}</Text>
                        <Text type="secondary">Runtime {data.sourceGroup.runtimeVersion}</Text>
                    </Space>
                </Descriptions.Item>
            )}
            {data?.productionGroupId && (
                <Descriptions.Item label="Production group">
                    <Text code copyable>
                        {data.productionGroupId}
                    </Text>
                </Descriptions.Item>
            )}
            <Descriptions.Item label="Production channel">{data?.productionChannel || 'Production'}</Descriptions.Item>
        </Descriptions>
    </div>
);

export const ExpoEASRolloutStep = ({ stepRunComponentProps, data }: ExpoEASRolloutStepProps) => {
    const currentPercentage = data?.currentRolloutPercentage;
    const halted = data?.halted === true;
    const [updateModalOpen, setUpdateModalOpen] = React.useState(false);
    const [targetPercentage, setTargetPercentage] = React.useState<number | null>(
        currentPercentage != null && currentPercentage < 100 ? currentPercentage + 1 : null,
    );
    const haltAutomation = useSelector(
        (state: RootState) =>
            selectAutomationsByType(
                state,
                stepRunComponentProps.appId,
                AutomationType.AutoHaltUnstablePhasedReleases,
            )?.[0],
    );
    const promoteAutomation = useSelector(
        (state: RootState) =>
            selectAutomationsByType(
                state,
                stepRunComponentProps.appId,
                AutomationType.AutoPromoteStablePhasedReleases,
            )?.[0],
    );
    const rolloutAutomations = React.useMemo(
        () => [haltAutomation, promoteAutomation].filter((automation) => automation != null),
        [haltAutomation, promoteAutomation],
    );
    const [resumeModalOpen, setResumeModalOpen] = React.useState(false);
    const [resumeAutomations, setResumeAutomations] = React.useState<AutomationType[]>([]);
    const [updateRollout, submitting, responseType, error] = useCallUpdatable(
        StepRunUpdatable.UpdateExpoEASRollout,
        stepRunComponentProps,
    );

    React.useEffect(() => {
        setTargetPercentage(currentPercentage != null && currentPercentage < 100 ? currentPercentage + 1 : null);
    }, [currentPercentage]);

    React.useEffect(() => {
        setResumeAutomations(rolloutAutomations.map((automation) => automation.type));
    }, [halted, rolloutAutomations]);

    React.useEffect(() => {
        if (responseType === RunwayResponseType.Success) {
            setUpdateModalOpen(false);
            setResumeModalOpen(false);
            globalMessage.success('Expo rollout updated.');
        }
    }, [responseType]);

    React.useEffect(() => {
        if (error) {
            globalMessage.error(error);
        }
    }, [error]);

    if (currentPercentage == null) {
        return (
            <Alert
                type="info"
                showIcon
                message="Rollout pending"
                description="Publish the Expo Release step to the Production channel before progressing this rollout."
            />
        );
    }

    const complete = currentPercentage === 100;
    const validTarget =
        targetPercentage != null &&
        Number.isInteger(targetPercentage) &&
        targetPercentage > currentPercentage &&
        targetPercentage <= 100;

    const updatePercentage = (percentage: number) => {
        if (complete || percentage <= currentPercentage || percentage > 100) return;
        const request: UpdateExpoEASRolloutRequest = {
            actionType: RolloutAction.UpdatePercentage,
            targetPercentage: percentage,
        };
        updateRollout(request);
    };

    const openUpdateModal = () => {
        setTargetPercentage(currentPercentage + 1);
        setUpdateModalOpen(true);
    };

    const resumeRollout = () => {
        updateRollout({
            actionType: RolloutAction.Resume,
            resumeAutomations,
        });
    };

    const statusText =
        complete ? 'COMPLETE'
        : halted ? 'HALTED'
        : 'IN PROGRESS';
    const statusColor =
        complete ? runwayColors.statusGreen
        : halted ? runwayColors.statusRed
        : runwayColors.statusYellow;

    return (
        <Flex vertical gap={20}>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: 20,
                    alignItems: 'stretch',
                }}
            >
                <RolloutStatusCard
                    integrationType={IntegrationType.ExpoEAS}
                    statusText={statusText}
                    statusColor={statusColor}
                >
                    <Flex vertical gap={16}>
                        <RolloutStatusMetric percentage={currentPercentage} />
                        <Progress
                            percent={currentPercentage}
                            showInfo={false}
                            strokeColor={halted ? runwayColors.statusRed : runwayColors.primary}
                            trailColor={antGrey.grey5}
                        />
                        {!complete && (
                            <RolloutStatusActions>
                                {halted ?
                                    <PermissionedActionButton
                                        actionType={RunwayUserAction.UAResumeRollout}
                                        releaseId={stepRunComponentProps.releaseId}
                                        icon={<PercentageOutlined style={{ color: runwayColors.info }} />}
                                        loading={submitting}
                                        disabled={submitting}
                                        customDisallowedMessage="You do not have permission to resume staged rollouts"
                                        style={rolloutActionButtonStyle}
                                        onClick={() => setResumeModalOpen(true)}
                                    >
                                        Resume staged rollout
                                    </PermissionedActionButton>
                                :   <PermissionedActionButton
                                        actionType={RunwayUserAction.UAUpdateRolloutPercentage}
                                        releaseId={stepRunComponentProps.releaseId}
                                        icon={<PercentageOutlined style={{ color: runwayColors.info }} />}
                                        loading={submitting}
                                        disabled={submitting}
                                        customDisallowedMessage="You do not have permission to edit rollouts"
                                        style={rolloutActionButtonStyle}
                                        onClick={openUpdateModal}
                                    >
                                        Update rollout
                                    </PermissionedActionButton>
                                }
                                <Popconfirm
                                    title="Are you sure you want to release to 100% now?"
                                    okText="Yes, release to 100% now."
                                    placement="topRight"
                                    okButtonProps={{ danger: true }}
                                    onConfirm={() => updatePercentage(100)}
                                >
                                    <PermissionedActionButton
                                        actionType={RunwayUserAction.UAUpdateRolloutPercentage}
                                        releaseId={stepRunComponentProps.releaseId}
                                        icon={<RiseOutlined style={{ color: runwayColors.statusGreen }} />}
                                        loading={submitting}
                                        disabled={submitting}
                                        customDisallowedMessage="You do not have permission to release to 100% of users"
                                        style={rolloutActionButtonStyle}
                                    >
                                        Release to 100% now
                                    </PermissionedActionButton>
                                </Popconfirm>
                            </RolloutStatusActions>
                        )}
                    </Flex>
                </RolloutStatusCard>
                <ExpoEASRolloutDetailsCard data={data} />
            </div>

            <Modal
                title="Update rollout"
                open={updateModalOpen}
                okText="Update rollout"
                confirmLoading={submitting}
                okButtonProps={{ disabled: !validTarget }}
                cancelButtonProps={{ disabled: submitting }}
                closable={!submitting}
                maskClosable={!submitting}
                onCancel={() => setUpdateModalOpen(false)}
                onOk={() => validTarget && updatePercentage(targetPercentage)}
            >
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text>Choose a percentage above the current {currentPercentage}% rollout.</Text>
                    <InputNumber
                        aria-label="Target rollout percentage"
                        min={currentPercentage + 1}
                        max={100}
                        precision={0}
                        value={targetPercentage}
                        onChange={(value) => setTargetPercentage(typeof value === 'number' ? value : null)}
                        addonAfter="%"
                        disabled={submitting}
                        autoFocus
                    />
                </Space>
            </Modal>
            {!complete && halted && (
                <Modal
                    title="Resume rollout"
                    open={resumeModalOpen}
                    okText="Resume rollout"
                    confirmLoading={submitting}
                    onCancel={() => setResumeModalOpen(false)}
                    onOk={resumeRollout}
                >
                    <Space direction="vertical">
                        <Text>Choose which rollout automations Runway should resume.</Text>
                        {rolloutAutomations.map((automation) => (
                            <Checkbox
                                key={automation.type}
                                checked={resumeAutomations.includes(automation.type)}
                                onChange={(event) =>
                                    setResumeAutomations((selected) =>
                                        event.target.checked ?
                                            [...selected, automation.type]
                                        :   selected.filter((type) => type !== automation.type),
                                    )
                                }
                            >
                                {automation.name}
                            </Checkbox>
                        ))}
                    </Space>
                </Modal>
            )}
        </Flex>
    );
};
